import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useApiData } from '../hooks/useApiData';
import {
  ScoreCard,
  ClientCell,
  FlagsCell,
  FilterChips,
  SeriesBars,
  LoadingState,
  ErrorState,
  EmptyState
} from '../components/primitives';
import RangeControl, { DEFAULT_RANGE, rangeLabel } from '../components/RangeControl';
import DimensionProfileChart, { ClientProfile as DimensionRow } from '../components/DimensionProfileChart';
import CoachingFlags from '../components/CoachingFlags';
import { codeOf, fmtInt, fmtMoney } from '../lib/format';
import { ClientSummary, RiskFlag, TimeRange, SeriesPoint } from '../types/superadmin';

type SeriesField = 'minutes' | 'sessions' | 'newUsers';

export default function Dashboard() {
  const [range, setRange] = useState<TimeRange>(DEFAULT_RANGE);
  // Internal testing tenants (dev/demo/apexia) are excluded from totals + lists
  // by default; toggling refetches with them included (the backend recomputes).
  const [includeInternal, setIncludeInternal] = useState(false);
  const { data, loading, error } = useApiData(
    () => api.getDashboard(range, includeInternal),
    [JSON.stringify(range), includeInternal]
  );
  // Per-client dimension scores for the coaching views. Fetched separately from
  // the dashboard summary because /clients is where the per-dimension breakdown
  // lives — the dashboard payload only carries the pooled call-quality number.
  const { data: clientsData } = useApiData(
    () => api.getClients(range),
    [JSON.stringify(range)]
  );

  const dimensionRows = useMemo<DimensionRow[]>(() => {
    const rows = clientsData?.data?.clients ?? [];
    return rows
      .filter((c) => includeInternal || !c.isInternal)
      .map((c) => ({
        tenantId: c.tenantId,
        name: c.displayName,
        dimensions: (c.dimensions ?? {}) as Record<string, number | null>,
        sessions: c.activity?.sessions ?? c.sessions ?? 0
      }))
      .filter((r) => (r.sessions ?? 0) > 0);
  }, [clientsData, includeInternal]);

  const [riskFilter, setRiskFilter] = useState<Set<string>>(new Set());
  // Default order is by risk (backend-sorted); the Client header sorts by name.
  const [nameDir, setNameDir] = useState<null | 'asc' | 'desc'>(null);
  const [expanded, setExpanded] = useState<{ label: string; field: SeriesField; unit?: string } | null>(null);

  const d = data?.data;
  const lastRun = data?.meta?.lastRun;

  const { filterOptions, filterCounts } = useMemo(() => {
    const options: Record<string, string> = {};
    const counts: Record<string, number> = {};
    (d?.atRiskClients || []).forEach((c: ClientSummary) => {
      c.flags.forEach((f: RiskFlag) => {
        options[f.key] = f.label;
        counts[f.key] = (counts[f.key] || 0) + 1;
      });
    });
    return {
      filterOptions: Object.entries(options).map(([id, label]) => ({ id, label })),
      filterCounts: counts
    };
  }, [d]);

  const filteredAtRisk = useMemo(() => {
    const rows = d?.atRiskClients || [];
    if (riskFilter.size === 0) return rows;
    return rows.filter((c) => c.flags.some((f) => riskFilter.has(f.key)));
  }, [d, riskFilter]);

  // Risk order by default (backend-sorted); alphabetical when the Client header
  // is clicked (asc -> desc -> back to risk order).
  const sortedAtRisk = useMemo(() => {
    if (!nameDir) return filteredAtRisk;
    return [...filteredAtRisk].sort((a, b) => {
      const cmp = a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' });
      return nameDir === 'desc' ? -cmp : cmp;
    });
  }, [filteredAtRisk, nameDir]);

  const cycleNameSort = () =>
    setNameDir((prev) => (prev === null ? 'asc' : prev === 'asc' ? 'desc' : null));
  const nameArrow = nameDir === 'asc' ? '↑' : nameDir === 'desc' ? '↓' : '↕';

  if (loading) return <LoadingState />;
  if (error || !d) return <ErrorState message={error || 'No data available.'} />;

  const { current, activity, series } = d;
  const seriesVals = (field: SeriesField) => series.map((s: SeriesPoint) => s[field]);
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const toggle = (id: string) =>
    setRiskFilter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      <div className="head">
        <div>
          <div className="eyebrow">Home · {today}</div>
          <h1 className="title">
            <span className="serif">
              {current.atRiskCount} client{current.atRiskCount === 1 ? '' : 's'} need a closer look
            </span>
            <br />
            <span className="serif" style={{ color: 'var(--ink-3)' }}>
              across {current.totalClients} live and {fmtInt(current.totalUsers)} users.
            </span>
          </h1>
          <p className="subtitle">
            Activity below covers <b>{rangeLabel(range)}</b>. At-risk, billing and totals reflect
            current state.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end' }}>
          <RangeControl value={range} onChange={setRange} />
          <button
            className={'filter-chip' + (includeInternal ? ' on' : '')}
            onClick={() => setIncludeInternal((v) => !v)}
            title="Dev/Test, Demo and Lushin are internal testing tenants, excluded from totals by default."
          >
            Show internal
          </button>
          {lastRun && <span className="freshness">Snapshot {lastRun.day}</span>}
        </div>
      </div>

      {/* Scorecard: current state + range activity */}
      <div className="scorecard">
        <ScoreCard label="Total clients" value={fmtInt(current.totalClients)} sub="Active clients." />
        <ScoreCard
          label="Total users"
          value={fmtInt(current.totalUsers)}
          sub={`${fmtInt(activity.newUsers)} new · ${rangeLabel(range)}`}
        />
        <ScoreCard label="MRR" prefix="$" value={fmtInt(current.mrr)} sub={`${fmtMoney(current.revenueLifetime)} lifetime`} />
        <ScoreCard
          label="Call minutes"
          value={fmtInt(activity.minutes)}
          sparkData={seriesVals('minutes')}
          sparkTone="pos"
          sub={`${rangeLabel(range)}`}
          onExpand={() => setExpanded({ label: 'Call minutes', field: 'minutes', unit: ' min' })}
        />
        <ScoreCard
          label="Sessions"
          value={fmtInt(activity.sessions)}
          sparkData={seriesVals('sessions')}
          sub={activity.callQualityScore != null ? `${activity.callQualityScore}% call quality` : 'no analyzed calls'}
          onExpand={() => setExpanded({ label: 'Sessions', field: 'sessions' })}
        />
        <ScoreCard label="At-risk clients" value={fmtInt(current.atRiskCount)} sub={`${fmtInt(current.openTickets)} open tickets`} />
      </div>

      {expanded && (
        <section className="section">
          <div className="section-head">
            <div>
              <h3>{expanded.label} · by day</h3>
              <p className="desc">{rangeLabel(range)}, across all clients.</p>
            </div>
            <div className="right">
              <button className="btn" onClick={() => setExpanded(null)}>
                Close ×
              </button>
            </div>
          </div>
          <SeriesBars data={series} field={expanded.field} unit={expanded.unit} />
        </section>
      )}

      {/* Coaching health — what the rubric says, before what billing says. */}
      <section className="section">
        <div className="section-head">
          <div>
            <h3>Where selling is breaking down</h3>
            <p className="desc">
              The five dimensions that decide a discovery call — the up-front
              contract at both ends, both pain categories, and what the prospect
              All ten rubric dimensions, scored out of 100, {rangeLabel(range)}.
              One line per client plus the all-client mean — a line that dips
              below the dashed soft-call mark is where that client is losing
              deals. Click a legend entry to isolate it.
            </p>
          </div>
        </div>
        <DimensionProfileChart clients={dimensionRows} />
      </section>

      <section className="section">
        <div className="section-head">
          <div>
            <h3>Coaching red flags</h3>
            <p className="desc">
              Those same five dimensions where a client sits below the rubric's
              60% soft-call line, worst first. These are value signals — they
              predict churn earlier than billing does, because a client whose
              reps never qualify pain is not a billing problem yet.
            </p>
          </div>
        </div>
        <CoachingFlags rows={dimensionRows} />
      </section>

      {/* At-risk clients (current state) */}
      <section className="section">
        <div className="section-head">
          <div>
            <h3>At-risk clients</h3>
            <p className="desc">Current health — billing, KB, adherence and inactivity signals. Click to drill in.</p>
          </div>
        </div>
        {filterOptions.length > 0 && (
          <FilterChips
            label="Red flags"
            options={filterOptions}
            counts={filterCounts}
            selected={riskFilter}
            onToggle={toggle}
            onClear={() => setRiskFilter(new Set())}
          />
        )}
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th
                  style={{ width: '28%' }}
                  className={'sortable' + (nameDir ? ' sorted' : '')}
                  onClick={cycleNameSort}
                >
                  Client <span className="sort-arr">{nameArrow}</span>
                </th>
                <th>Red flags</th>
                <th style={{ width: 120, textAlign: 'right' }}>Drill in</th>
              </tr>
            </thead>
            <tbody>
              {sortedAtRisk.map((c) => (
                <tr key={c.tenantId} className={c.isInternal ? 'internal-row' : undefined}>
                  <td>
                    <ClientCell
                      name={c.displayName}
                      code={codeOf(c.tenantId)}
                      sub={`${fmtInt(c.users)} users · ${c.subscriptionStatus}`}
                    />
                  </td>
                  <td>
                    <FlagsCell flags={c.flags} />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Link className="action-btn" to={`/clients/${c.tenantId}`}>
                      View →
                    </Link>
                  </td>
                </tr>
              ))}
              {filteredAtRisk.length === 0 && (
                <tr>
                  <td colSpan={3}>
                    <EmptyState>
                      No clients match the selected flags. <b>Clear filters</b> to see all.
                    </EmptyState>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Top clients (range) + open tickets (current) */}
      <section className="section">
        <div className="split">
          <div>
            <div className="section-head">
              <div>
                <h3>Top clients</h3>
                <p className="desc">Most active by call minutes, {rangeLabel(range)}.</p>
              </div>
            </div>
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '50%' }}>Client</th>
                    <th>Activity</th>
                    <th style={{ width: 90, textAlign: 'right' }}>View</th>
                  </tr>
                </thead>
                <tbody>
                  {d.topClients.map((c) => (
                    <tr key={c.tenantId}>
                      <td>
                        <ClientCell name={c.displayName} code={codeOf(c.tenantId)} />
                      </td>
                      <td>
                        <div className="flags">
                          <span className="chip pos">{fmtInt(c.minutes)} min</span>
                          <span className="chip info">{fmtInt(c.sessions)} sessions</span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <Link className="action-btn" to={`/clients/${c.tenantId}`}>
                          →
                        </Link>
                      </td>
                    </tr>
                  ))}
                  {d.topClients.length === 0 && (
                    <tr>
                      <td colSpan={3}>
                        <EmptyState>No activity in this range.</EmptyState>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div className="section-head">
              <div>
                <h3>Open tickets</h3>
                <p className="desc">Oldest first, current.</p>
              </div>
            </div>
            <div className="rank">
              {d.openTickets.length === 0 && (
                <EmptyState>
                  <b>No open tickets.</b> All clear.
                </EmptyState>
              )}
              {d.openTickets.map((ticket, i) => (
                <div className="rank-row" key={ticket.ticketId}>
                  <div className="pos">{String(i + 1).padStart(2, '0')}</div>
                  <div>
                    <div className="name">{ticket.title}</div>
                    <div className="meta">
                      {ticket.displayName} · {ticket.name || ticket.email || 'unknown'}
                    </div>
                  </div>
                  <div className="stat">
                    <span className={'chip ' + (ticket.severity === 'high' ? 'neg' : ticket.severity === 'medium' ? 'warn' : '')}>
                      {ticket.severity}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
